/* eslint-disable */
import React, { useMemo } from "react";
import {
  Box,
  Flex,
  Icon,
  Text,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { NavLink } from "react-router-dom";
import { Scrollbars } from "react-custom-scrollbars-2";
import {
  renderThumb,
  renderTrack,
  renderView,
} from "components/scrollbar/Scrollbar";
import { IoMenuOutline } from "react-icons/io5";
import PropTypes from "prop-types";
import AdminNavbarLinks from "components/navbar/NavbarLinksAdmin";

// --- 유틸: routes 분리 ---
function splitRoutes(routes = []) {
  const nav = [];
  const menu = [];
  routes.forEach((r) => {
    const isNav =
      r.isNavigator === true ||
      r.tag === "navigator" ||
      r.category === "navigator";
    (isNav ? nav : menu).push(r);
  });
  return { nav, menu };
}

// --- 단일 항목 렌더러 ---
function SidebarItem({ route, onClick }) {
  const activeBg = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const color = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("gray.500", "gray.300");

  const to = (route.layout || "") + (route.path || "");

  return (
    <NavLink
      to={to}
      style={{ width: "100%" }}
      onClick={onClick}
      className={({ isActive }) => (isActive ? "active" : "")}
    >
      {({ isActive }) => (
        <HStack
          w="100%"
          px="12px"
          py="10px"
          borderRadius="12px"
          transition="all .2s"
          bg={isActive ? activeBg : "transparent"}
          _hover={{ bg: hoverBg }}
          spacing="10px"
          align="center"
        >
          {route.icon && (
            <Icon
              as={route.icon}
              boxSize="18px"
              color={iconColor}
              flexShrink={0}
            />
          )}
          <Text
            color={color}
            fontSize="sm"
            fontWeight={isActive ? 700 : 500}
            noOfLines={1}
          >
            {route.name || route.title}
          </Text>
        </HStack>
      )}
    </NavLink>
  );
}

// --- 섹션 렌더러 ---
function SidebarSection({ title, routes, onItemClick }) {
  const titleColor = useColorModeValue("gray.500", "gray.400");
  return (
    <VStack align="stretch" spacing="10px">
      <Text fontSize="xs" fontWeight="700" color={titleColor} px="4px">
        {title}
      </Text>
      <Divider />
      <VStack align="stretch" spacing="6px">
        {routes.map((r) => (
          <SidebarItem key={(r.key || r.name || r.path) + title} route={r} onClick={onItemClick} />
        ))}
      </VStack>
    </VStack>
  );
}

// =======================
// 데스크톱 사이드바
// =======================
function Sidebar({ routes, showUpgrade = false, brandTitle }) {
  const sidebarBg = useColorModeValue("white", "navy.800");
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.08)",
    "unset"
  );

  const { nav, menu } = useMemo(() => splitRoutes(routes), [routes]);

  return (
    <Box
      display={{ base: "none", xl: "block" }}   //  데스크탑에서만 표시
      position="fixed"
      top="0"
      left="0"
      h="100vh"
      w="300px"
      bg={sidebarBg}
      boxShadow={shadow}
      overflow="hidden"
      zIndex="100"
    >
      {/*  사이드바 상단(메뉴 위)에 표시 - sticky 영역 */}
      <Box
        w={{ base: "auto", xl: "100%" }}   // 데스크탑에서만 100% 너비
        px={{ base: "16px", xl: "0" }}     // 데스크탑은 좌우 패딩 제거
        pt="16px"
        pb="12px"
        mb={{ base: "0", xl: "12px" }}     // 데스크탑에서만 아래 여백
        position="sticky"
        top="0"
        zIndex={1}
        bg={sidebarBg}
        borderRadius={{ base: "16px", xl: "0" }}   // 데스크탑은 모서리 제거
        boxShadow={{ base: "base", xl: "none" }}   // 데스크탑은 그림자 제거
      >
        <AdminNavbarLinks />
      </Box>

      <Scrollbars
        autoHide
        renderTrackVertical={renderTrack}
        renderThumbVertical={renderThumb}
        renderView={renderView}
      >
        <VStack align="stretch" spacing="18px" p="16px" pt="0">
          {nav.length > 0 && <SidebarSection title="네비게이터" routes={nav} />}
          {menu.length > 0 && <SidebarSection title="메뉴" routes={menu} />}
        </VStack>
      </Scrollbars>
    </Box>
  );
}

// =======================
// 모바일: 햄버거 버튼
// =======================
export function SidebarDrawerButton({ onOpen }) {
  const menuColor = useColorModeValue("gray.400", "white");
  return (
    <Flex display={{ base: "flex", xl: "none" }} align="center" onClick={onOpen}>
      <Icon
        as={IoMenuOutline}
        color={menuColor}
        w="20px"
        h="20px"
        _hover={{ cursor: "pointer" }}
      />
    </Flex>
  );
}

// =======================
// 모바일: Drawer (모바일은 상단 Navbar에서 이미 표시되므로 여기선 추가 안 함)
// =======================
export function SidebarDrawer({
  isOpen,
  onClose,
  routes,
  showUpgrade = false,
  brandTitle,
}) {
  const sidebarBg = useColorModeValue("white", "navy.800");
  const { nav, menu } = useMemo(() => splitRoutes(routes), [routes]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement={document.documentElement.dir === "rtl" ? "right" : "left"}
    >
      <DrawerOverlay />
      <DrawerContent
        w="285px"
        maxW="285px"
        bg={sidebarBg}
        position="relative"
        overflow="hidden"
      >
        <DrawerCloseButton
          position="absolute"
          top="12px"
          right="12px"
          zIndex={10}
          pointerEvents="auto"
          _focus={{ boxShadow: "none" }}
          _hover={{ boxShadow: "none" }}
        />
        <DrawerBody px="16px" pb="16px" pt="56px" position="relative" zIndex={0}>
          <Scrollbars
            autoHide
            renderTrackVertical={renderTrack}
            renderThumbVertical={renderThumb}
            renderView={renderView}
          >
            <VStack align="stretch" spacing="18px" pr="8px">
              {nav.length > 0 && (
                <SidebarSection
                  title="네비게이터"
                  routes={nav}
                  onItemClick={onClose}
                />
              )}
              {menu.length > 0 && (
                <SidebarSection
                  title="메뉴"
                  routes={menu}
                  onItemClick={onClose}
                />
              )}
            </VStack>
          </Scrollbars>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  showUpgrade: PropTypes.bool,
  brandTitle: PropTypes.string,
};

export default Sidebar;
